import type { ComponentProps } from 'react'
import { TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

/**
 * A table row that opens something when it is clicked — and when Enter or Space is pressed on it.
 *
 * A row with only an `onClick` is a control that exists for the mouse alone: somebody working
 * through two hundred leads from the keyboard can reach the action menu at the end of each row and
 * nothing else. `tabIndex` plus the two keys is the smallest thing that fixes that without
 * pretending a `<tr>` is a button, which would take the table's own semantics away from a screen
 * reader.
 */
export function ActivatableRow({
  onActivate,
  className,
  children,
  ...props
}: ComponentProps<typeof TableRow> & { onActivate: () => void }) {
  return (
    <TableRow
      tabIndex={0}
      onClick={onActivate}
      onKeyDown={(event) => {
        // A control inside the row has the focus — the menu, a checkbox. Enter belongs to it.
        if (event.target !== event.currentTarget) {
          return
        }
        if (event.key === 'Enter' || event.key === ' ') {
          // Space scrolls the page otherwise, which is the opposite of what was asked for.
          event.preventDefault()
          onActivate()
        }
      }}
      className={cn(
        'cursor-pointer focus-visible:bg-muted/50 focus-visible:outline-ring focus-visible:outline-2 focus-visible:-outline-offset-2',
        className,
      )}
      {...props}
    >
      {children}
    </TableRow>
  )
}
